import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useQuery, useMutation, useQueryClient } from 'react-query';
import api from '../../services/api';
import { useAuthStore } from '../../store/authStore';
import { pbxWS } from '../../services/websocket';
import toast from 'react-hot-toast';
import { useConfirm } from '../common/ConfirmDialog';
import { spyWithWebphone } from './spyWithWebphone';
import { openOrFocusWebphone } from '../../utils/singleWebphone';
import {
  Phone, PhoneOff, PhoneIncoming, PhoneOutgoing, PhoneMissed,
  Eye, MessageSquare, Users, Activity, Radio,
  CheckCircle, Clock, XCircle, Coffee, Briefcase,
  Volume2, VolumeX, Mic, MicOff, AlertCircle,
  RefreshCw, Headphones, UserCheck,
  ArrowRightLeft, ParkingSquare, StopCircle, MoreVertical
} from 'lucide-react';

// ── Helpers ──────────────────────────────────────────────────
function formatDuration(startMs, answerMs) {
  if (!answerMs) return null;
  const secs = Math.floor((Date.now() - answerMs) / 1000);
  const m = Math.floor(secs / 60).toString().padStart(2, '0');
  const s = (secs % 60).toString().padStart(2, '0');
  return `${m}:${s}`;
}

function useTick(interval = 1000) {
  const [, setTick] = useState(0);
  useEffect(() => {
    const id = setInterval(() => setTick(t => t + 1), interval);
    return () => clearInterval(id);
  }, [interval]);
}

const PRESENCE_MAP = {
  available: { label: 'Disponible', color: 'text-green-400', dot: 'bg-green-400', icon: CheckCircle },
  away: { label: 'Ausente', color: 'text-yellow-400', dot: 'bg-yellow-400', icon: Clock },
  dnd: { label: 'No molestar', color: 'text-red-400', dot: 'bg-red-400', icon: XCircle },
  lunch: { label: 'Almuerzo', color: 'text-orange-400', dot: 'bg-orange-400', icon: Coffee },
  business_trip: { label: 'Viaje', color: 'text-blue-400', dot: 'bg-blue-400', icon: Briefcase },
};

const CALL_STATE_MAP = {
  ringing: { label: 'Timbrando', color: 'text-yellow-400', bg: 'bg-yellow-400/10 border-yellow-400/30' },
  dialing: { label: 'Marcando', color: 'text-blue-400', bg: 'bg-blue-400/10 border-blue-400/30' },
  active: { label: 'En llamada', color: 'text-green-400', bg: 'bg-green-400/10 border-green-400/30' },
};

// ── CallTimer ─────────────────────────────────────────────────
function CallTimer({ answerTime }) {
  useTick(1000);
  if (!answerTime) return <span className="text-yellow-400 text-xs animate-pulse">Timbrando...</span>;
  const secs = Math.floor((Date.now() - answerTime) / 1000);
  const m = Math.floor(secs / 60).toString().padStart(2, '0');
  const s = (secs % 60).toString().padStart(2, '0');
  return <span className="text-green-400 font-mono text-sm font-semibold">{m}:{s}</span>;
}

// ── ExtensionCard ─────────────────────────────────────────────
function ExtensionCard({ ext, onSpy, onWhisper, onBarge, onHangup, onMenu, supervisorExt, canMonitor }) {
  const presence = PRESENCE_MAP[ext.presence_status] || PRESENCE_MAP.available;
  const PreIcon = presence.icon;
  const hasActiveCalls = ext.activeCalls?.length > 0;
  const primaryCall = ext.activeCalls?.[0];
  const callState = primaryCall ? CALL_STATE_MAP[primaryCall.state] : null;

  return (
    <div className={`relative rounded-xl border transition-all duration-300 overflow-hidden ${
      hasActiveCalls && primaryCall?.state === 'active'
        ? 'border-green-500/40 bg-green-900/10 shadow-lg shadow-green-900/20'
        : hasActiveCalls
        ? 'border-yellow-500/40 bg-yellow-900/10'
        : 'border-gray-800 bg-gray-900/50 hover:border-gray-700'
    }`}>

      {/* Indicador de actividad (pulso) */}
      {hasActiveCalls && primaryCall?.state === 'active' && (
        <div className="absolute top-2 right-2 flex h-2 w-2">
          <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-green-400 opacity-75" />
          <span className="relative inline-flex rounded-full h-2 w-2 bg-green-500" />
        </div>
      )}

      {/* Header de la tarjeta: avatar + nombre + estado */}
      <div className="p-3.5 flex items-start gap-3">
        <div className={`w-11 h-11 rounded-full flex items-center justify-center shrink-0 text-[13px] font-semibold
          bg-gray-800 text-gray-300 ring-2 ${
            hasActiveCalls && primaryCall?.state === 'active' ? 'ring-green-500/70'
            : hasActiveCalls ? 'ring-yellow-500/70'
            : presence.ring || 'ring-gray-700'
          }`}>
          {(ext.caller_id_name || ext.number || '?')
            .split(' ').filter(Boolean).map(w => w[0]).slice(0, 2).join('').toUpperCase()}
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-center justify-between gap-2">
            <p className="font-semibold text-white text-[15px] leading-tight truncate">
              {ext.caller_id_name || ext.number}
            </p>
            <span className="font-mono text-[11px] text-muted shrink-0">{ext.number}</span>
          </div>
          <div className="flex items-center gap-1.5 mt-1">
            <span className={`w-1.5 h-1.5 rounded-full shrink-0 ${
              hasActiveCalls && primaryCall?.state === 'active' ? 'bg-green-400'
              : hasActiveCalls ? 'bg-yellow-400 animate-pulse'
              : presence.dot
            }`} />
            <span className={`text-xs truncate ${
              hasActiveCalls && primaryCall?.state === 'active' ? 'text-green-400'
              : hasActiveCalls ? 'text-yellow-400'
              : presence.color
            }`}>
              {hasActiveCalls && primaryCall?.state === 'active' ? 'En llamada'
                : hasActiveCalls ? 'Timbrando'
                : presence.label}
            </span>
            {!hasActiveCalls && <PreIcon size={10} className={`${presence.color} shrink-0`} />}
            {ext.queue_status === 'logged_out' && (
              <span className="text-[11px] text-muted shrink-0">· Fuera</span>
            )}
          </div>
        </div>
      </div>

      {/* Llamada activa */}
      {primaryCall && (
        <div className={`mx-3 mb-3 p-2.5 rounded-lg border ${callState?.bg || 'bg-gray-800 border-gray-700'}`}
             onContextMenu={(e) => primaryCall.channel && onMenu?.(e, primaryCall)}>
          <div className="flex items-center justify-between mb-1.5">
            <div className="flex items-center gap-1.5">
              {primaryCall.direction === 'inbound'
                ? <PhoneIncoming size={11} className="text-blue-400" />
                : <PhoneOutgoing size={11} className="text-orange-400" />
              }
              <span className={`text-xs font-medium ${callState?.color || 'text-gray-300'}`}>
                {callState?.label || primaryCall.state}
              </span>
            </div>
            <CallTimer answerTime={primaryCall.answerTime} />
          </div>
          <div className="text-xs text-gray-300 font-mono truncate">
            {primaryCall.callerName && primaryCall.callerName !== '<unknown>'
              ? `${primaryCall.callerName} <${primaryCall.callerNum}>`
              : primaryCall.callerNum || 'Desconocido'
            }
          </div>
          {primaryCall.onHold && (
            <div className="mt-1 flex items-center gap-1 text-yellow-400 text-xs">
              <VolumeX size={10} /> En espera
            </div>
          )}

          {/* Botones de acción de monitoreo */}
          {canMonitor && primaryCall.state === 'active' && primaryCall.channel && (
            <div className="flex gap-1.5 mt-2.5 pt-2 border-t border-gray-700/50">
              <button
                onClick={() => onSpy(primaryCall.channel)}
                title="Escuchar (solo supervisor oye)"
                className="flex-1 flex items-center justify-center gap-1 py-1.5 rounded-lg text-xs bg-blue-900/40 hover:bg-blue-900/70 text-blue-300 hover:text-blue-200 border border-blue-700/30 transition-colors">
                <Headphones size={11} /> Escuchar
              </button>
              <button
                onClick={() => onWhisper(primaryCall.channel)}
                title="Susurrar (solo agente oye al supervisor)"
                className="flex-1 flex items-center justify-center gap-1 py-1.5 rounded-lg text-xs bg-purple-900/40 hover:bg-purple-900/70 text-purple-300 hover:text-purple-200 border border-purple-700/30 transition-colors">
                <MessageSquare size={11} /> Susurrar
              </button>
              <button
                onClick={() => onBarge(primaryCall.channel)}
                title="Irrumpir (todos se oyen)"
                className="flex-1 flex items-center justify-center gap-1 py-1.5 rounded-lg text-xs bg-orange-900/40 hover:bg-orange-900/70 text-orange-300 hover:text-orange-200 border border-orange-700/30 transition-colors">
                <Users size={11} /> Irrumpir
              </button>
              <button
                onClick={(e) => onMenu?.(e, primaryCall)}
                title="Más acciones (transferir, aparcar, colgar…)"
                className="flex items-center justify-center px-2 py-1.5 rounded-lg text-xs bg-gray-800 hover:bg-gray-700 text-gray-400 hover:text-white border border-gray-700/50 transition-colors">
                <MoreVertical size={13} />
              </button>
            </div>
          )}
          {!canMonitor && (
            <p className="text-xs text-muted mt-1.5 text-center">Sin permiso de monitoreo</p>
          )}
        </div>
      )}

      {/* Sin llamada activa */}
      {!hasActiveCalls && (
        <div className="px-3 pb-3">
          <div className="flex items-center gap-1.5 text-xs text-muted">
            <Radio size={10} />
            <span>{ext.queue_status === 'logged_in' ? 'En espera de llamadas' : 'Fuera de servicio'}</span>
          </div>
        </div>
      )}
    </div>
  );
}

// ── ActiveCallsTable ──────────────────────────────────────────
function ActiveCallsTable({ calls, onSpy, onWhisper, onBarge, onHangup, onRowMenu }) {
  useTick(1000);

  if (calls.length === 0) {
    return (
      <div className="text-center py-8 text-muted text-sm">
        No hay llamadas activas en este momento
      </div>
    );
  }

  return (
    <div className="overflow-x-auto">
      <table className="w-full text-sm">
        <thead>
          <tr>
            {['Extensión', 'Origen', 'Destino', 'Estado', 'Duración', 'Canal', 'Acciones'].map(h => (
              <th key={h} className="table-th text-xs">{h}</th>
            ))}
          </tr>
        </thead>
        <tbody>
          {calls.map(call => {
            const st = CALL_STATE_MAP[call.state] || {};
            return (
              <tr key={call.uniqueId} className="table-row"
                  onContextMenu={(e) => call.channel && onRowMenu?.(e, call)}>
                <td className="table-td font-mono font-bold text-white">{call.extNumber}</td>
                <td className="table-td">
                  <div className="text-gray-200">{call.callerNum || '—'}</div>
                  {call.callerName && call.callerName !== '<unknown>' && (
                    <div className="text-xs text-muted">{call.callerName}</div>
                  )}
                </td>
                <td className="table-td text-gray-300">{call.destNum || '—'}</td>
                <td className="table-td">
                  <span className={`text-xs font-medium ${st.color || 'text-gray-400'}`}>
                    {st.label || call.state}
                  </span>
                </td>
                <td className="table-td">
                  <CallTimer answerTime={call.answerTime} />
                </td>
                <td className="table-td">
                  <span className="text-xs font-mono text-muted truncate max-w-[140px] block">
                    {call.channel?.replace('PJSIP/', '') || '—'}
                  </span>
                </td>
                <td className="table-td">
                  {call.state === 'active' && call.channel && (
                    <div className="flex items-center gap-1">
                      <button onClick={() => onSpy(call.channel)} title="Escuchar"
                        className="p-1.5 rounded-lg bg-blue-900/30 text-blue-400 hover:bg-blue-900/60 transition-colors">
                        <Headphones size={13} />
                      </button>
                      <button onClick={() => onWhisper(call.channel)} title="Susurrar"
                        className="p-1.5 rounded-lg bg-purple-900/30 text-purple-400 hover:bg-purple-900/60 transition-colors">
                        <MessageSquare size={13} />
                      </button>
                      <button onClick={() => onBarge(call.channel)} title="Irrumpir"
                        className="p-1.5 rounded-lg bg-orange-900/30 text-orange-400 hover:bg-orange-900/60 transition-colors">
                        <Users size={13} />
                      </button>
                      <button onClick={() => onHangup(call.channel)} title="Colgar"
                        className="p-1.5 rounded-lg bg-red-900/30 text-red-400 hover:bg-red-900/60 transition-colors">
                        <PhoneOff size={13} />
                      </button>
                      <button onClick={(e) => onRowMenu?.(e, call)} title="Más acciones"
                        className="p-1.5 rounded-lg bg-gray-800 text-gray-400 hover:bg-gray-700 hover:text-white transition-colors">
                        <MoreVertical size={13} />
                      </button>
                    </div>
                  )}
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}

// ── Menú contextual de acciones de llamada ────────────────────
function CallContextMenu({ menu, onClose, actions }) {
  const ref = useRef(null);
  useEffect(() => {
    if (!menu.open) return;
    const onDoc = (e) => { if (ref.current && !ref.current.contains(e.target)) onClose(); };
    const onEsc = (e) => { if (e.key === 'Escape') onClose(); };
    document.addEventListener('mousedown', onDoc);
    document.addEventListener('keydown', onEsc);
    return () => { document.removeEventListener('mousedown', onDoc); document.removeEventListener('keydown', onEsc); };
  }, [menu.open, onClose]);

  if (!menu.open || !menu.call) return null;
  const call = menu.call;
  const destLabel = call.destNum || call.callerNum || 'destino';

  // Ajustar posición para no salir de la pantalla
  const style = {
    top: Math.min(menu.y, window.innerHeight - 340),
    left: Math.min(menu.x, window.innerWidth - 260),
  };

  const items = [
    { key: 'barge',    icon: Users,          label: 'Irrumpir',                    onClick: () => actions.barge(call) },
    { key: 'listen',   icon: Headphones,     label: 'Escuchar',                    onClick: () => actions.listen(call) },
    { key: 'whisper',  icon: MessageSquare,  label: `Susurrar a ${call.extNumber || 'agente'}`, onClick: () => actions.whisper(call) },
    { sep: true },
    { key: 'transfer', icon: ArrowRightLeft, label: `Transferir ${destLabel}`,     onClick: () => actions.transfer(call) },
    { key: 'park',     icon: ParkingSquare,  label: `Aparcar ${destLabel}`,        onClick: () => actions.park(call) },
    { sep: true },
    { key: 'hangup',   icon: PhoneOff,       label: 'Cortar',                      danger: true, onClick: () => actions.hangup(call) },
    { key: 'stoprec',  icon: StopCircle,     label: 'Detener la grabación',        onClick: () => actions.stopRecording(call) },
  ];

  return (
    <div
      ref={ref}
      className="fixed z-50 min-w-[230px] py-1.5 bg-gray-900 border border-gray-700 rounded-xl shadow-2xl shadow-black/50"
      style={style}
    >
      {items.map((it, i) =>
        it.sep ? (
          <div key={`sep-${i}`} className="my-1 border-t border-gray-800" />
        ) : (
          <button
            key={it.key}
            onClick={() => { it.onClick(); onClose(); }}
            className={`w-full flex items-center gap-3 px-4 py-2 text-sm text-left transition-colors
              ${it.danger ? 'text-red-400 hover:bg-red-900/30' : 'text-gray-200 hover:bg-gray-800'}`}
          >
            <it.icon size={15} className="shrink-0 opacity-80" />
            <span className="truncate">{it.label}</span>
          </button>
        )
      )}
    </div>
  );
}

// ── MonitorPage (principal) ───────────────────────────────────
export default function MonitorPage() {
  const confirm = useConfirm();
  const { user } = useAuthStore();
  const qc = useQueryClient();
  // Estado de escucha: el audio de la escucha ya no llega a esta pestaña
  // (el softphone vive solo en /webphone, ver App.js). Aquí solo se rastrea
  // si el backend confirmó que la escucha está activa.
  const [listeningChannel, setListeningChannel] = useState(null);
  const isListening = !!listeningChannel;
  const wsRef = useRef(null);
  const [view, setView] = useState('grid'); // grid | table
  const [extensions, setExtensions] = useState([]);
  const [activeCalls, setActiveCalls] = useState([]);
  const [wsConnected, setWsConnected] = useState(false);
  const [supervisorExt, setSupervisorExt] = useState('');

  // El usuario puede monitorear si es superadmin/admin/supervisor o tiene el permiso monitor.control
  const userCanMonitor = ['superadmin', 'admin', 'supervisor'].includes(user?.role)
    || !!user?.permissions?.['monitor.control'];

  // Extensión de supervisor: SIEMPRE automática, nunca a mano — la propia
  // extensión asignada al usuario logueado.
  useEffect(() => {
    if (!supervisorExt && user?.extensionNumber) setSupervisorExt(String(user.extensionNumber));
  }, [user, supervisorExt]);

  // Cargar datos iniciales
  // isError faltaba: un fallo real de red caía en el mismo vacío "No hay
  // llamadas activas" que un PBX sano sin tráfico mostraría — exactamente
  // el escenario del que depende el Principio #2 del producto
  // (/impeccable critique, 30-sep-2026 — P0).
  const { data, isLoading, isError, refetch } = useQuery(
    'monitor-calls',
    () => api.get('/monitor/calls').then(r => r.data),
    {
      refetchInterval: 5000,
      onSuccess: (d) => {
        setExtensions(d.extensions || []);
        setActiveCalls(d.activeCalls || []);
      },
    }
  );

  // La suscripción al WebSocket compartido (pbxWS) se hace más abajo,
  // tras definir handleWsEvent (para no romper el orden de declaración).

  const handleWsEvent = useCallback((msg) => {
    if (!['call.new', 'call.update', 'call.answered', 'call.hangup', 'call.hold'].includes(msg.type)) return;
    const call = msg.data;

    setActiveCalls(prev => {
      if (msg.type === 'call.hangup') {
        return prev.filter(c => c.uniqueId !== call.uniqueId);
      }
      const existing = prev.findIndex(c => c.uniqueId === call.uniqueId);
      if (existing >= 0) {
        const updated = [...prev];
        updated[existing] = call;
        return updated;
      }
      if (msg.type === 'call.new') return [...prev, call];
      return prev;
    });

    // Actualizar extensiones
    setExtensions(prev => prev.map(ext => {
      if (ext.number !== call.extNumber) return ext;
      if (msg.type === 'call.hangup') {
        return { ...ext, activeCalls: (ext.activeCalls || []).filter(c => c.uniqueId !== call.uniqueId) };
      }
      const existingIdx = (ext.activeCalls || []).findIndex(c => c.uniqueId === call.uniqueId);
      if (existingIdx >= 0) {
        const updated = [...ext.activeCalls];
        updated[existingIdx] = call;
        return { ...ext, activeCalls: updated };
      }
      return { ...ext, activeCalls: [...(ext.activeCalls || []), call] };
    }));
  }, []);

  // WebSocket en tiempo real — reutiliza la conexión compartida pbxWS
  // (que usa el token de sesión correcto), en vez de abrir una propia.
  useEffect(() => {
    const types = ['call.new', 'call.update', 'call.answered', 'call.hangup', 'call.hold'];
    const offs = types.map(t => pbxWS.on(t, (data) => handleWsEvent({ type: t, data })));
    const offConn = pbxWS.on('connected', () => setWsConnected(true));
    const offDisc = pbxWS.on('disconnected', () => setWsConnected(false));
    // Estado inicial según el socket ya abierto
    setWsConnected(pbxWS.ws?.readyState === 1);
    return () => { offs.forEach(o => o && o()); offConn(); offDisc(); };
  }, [handleWsEvent]);

  // Acciones de monitoreo
  // Si el softphone del supervisor no está conectado (409), abre el Marcador solo y reintenta hasta que registre.
  const spyMut = useMutation(
    ({ channel, mode }) => {
      const post = () => api.post('/monitor/spy', { supervisorExt, targetChannel: channel, mode }).then(r => r.data);
      const openWebphone = () => {
        const w = openOrFocusWebphone();   // reutiliza el Marcador si ya está abierto (una sola ventana)
        if (w) toast.loading('Abriendo el Marcador…', { id: 'webphone-open' });
        return w;
      };
      return spyWithWebphone(post, openWebphone).finally(() => toast.dismiss('webphone-open'));
    },
    {
      onSuccess: (_, { channel, mode }) => {
        const labels = { listen: 'Escucha iniciada', whisper: 'Susurro iniciado', barge: 'Irrumpiendo en llamada' };
        toast.success(labels[mode] || 'Monitor iniciado');
        if (mode === 'listen') setListeningChannel(channel);
      },
      onError: (e) => toast.error(e.response?.data?.error || 'Error al monitorear'),
    }
  );

  const hangupMut = useMutation(
    (channel) => api.post('/monitor/hangup', { channel }).then(r => r.data),
    {
      onSuccess: (_, channel) => {
        toast.success('Llamada colgada');
        if (channel === listeningChannel) setListeningChannel(null);
      },
      onError: (e) => toast.error(e.response?.data?.error || 'Error'),
    }
  );

  // Detener la escucha: le pide al backend que cuelgue el ChanSpy. El audio
  // de la escucha suena en la pestaña /webphone del supervisor, no en esta.
  const stopSpyMut = useMutation(
    () => api.post('/monitor/stop-spy', { supervisorExt }).then(r => r.data),
    {
      onSuccess: () => { toast.success('Escucha detenida'); setListeningChannel(null); },
      onError: (e) => toast.error(e.response?.data?.error || 'Error al detener'),
    }
  );

  const transferMut = useMutation(
    ({ channel, destExten }) => api.post('/monitor/transfer', { channel, destExten }).then(r => r.data),
    {
      onSuccess: (_, { destExten }) => toast.success(`Transferido a ${destExten}`),
      onError: (e) => toast.error(e.response?.data?.error || 'Error al transferir'),
    }
  );

  const parkMut = useMutation(
    (channel) => api.post('/monitor/park', { channel }).then(r => r.data),
    {
      onSuccess: () => toast.success('Llamada aparcada'),
      onError: (e) => toast.error(e.response?.data?.error || 'Error al aparcar'),
    }
  );

  const recordingMut = useMutation(
    ({ channel, action }) => api.post('/monitor/recording', { channel, action }).then(r => r.data),
    {
      onSuccess: (_, { action }) => toast.success(action === 'stop' ? 'Grabación detenida' : 'Grabación iniciada'),
      onError: (e) => toast.error(e.response?.data?.error || 'Error con la grabación'),
    }
  );

  const doAction = async (channel, mode) => {
    if (!supervisorExt) return toast.error('Selecciona tu extensión de supervisor primero');
    // Irrumpir hace audible al supervisor de inmediato para el cliente, en
    // una llamada real — confirmar acá cubre los 3 puntos donde se llama
    // doAction(ch,'barge') (tarjeta, tabla, menú contextual) de una sola vez
    // (/impeccable critique, 30-sep-2026 — P0).
    if (mode === 'barge' && !(await confirm({ title: 'Irrumpir en la llamada', message: 'El cliente te oirá de inmediato.', confirmLabel: 'Irrumpir', danger: true }))) return;
    spyMut.mutate({ channel, mode });
  };

  // Colgar es irreversible sobre una llamada real — mismo criterio que
  // barge arriba, un solo lugar para los 3 puntos que llaman a hangupMut
  // directo (tarjeta, tabla, menú contextual).
  const confirmedHangup = async (channel) => {
    if (!(await confirm({ title: 'Colgar la llamada', message: 'La llamada se cortará para todos los participantes.', confirmLabel: 'Colgar', danger: true }))) return;
    hangupMut.mutate(channel);
  };

  const stopListening = () => {
    if (supervisorExt) stopSpyMut.mutate();
  };

  // ── Menú contextual ──────────────────────────────────────────
  const [ctxMenu, setCtxMenu] = useState({ open: false, x: 0, y: 0, call: null });

  const openCtxMenu = useCallback((e, call) => {
    e.preventDefault();
    if (!userCanMonitor) return;   // solo superadmin/admin/supervisor
    setCtxMenu({ open: true, x: e.clientX, y: e.clientY, call });
  }, [userCanMonitor]);

  const closeCtxMenu = useCallback(() => setCtxMenu(m => ({ ...m, open: false })), []);

  const ctxActions = {
    barge:   (call) => doAction(call.channel, 'barge'),
    listen:  (call) => doAction(call.channel, 'listen'),
    whisper: (call) => doAction(call.channel, 'whisper'),
    hangup:  (call) => confirmedHangup(call.channel),
    park:    (call) => parkMut.mutate(call.channel),
    stopRecording: (call) => recordingMut.mutate({ channel: call.channel, action: 'stop' }),
    transfer: (call) => {
      const dest = window.prompt('Transferencia ciega — extensión o número destino:');
      if (dest && dest.trim()) {
        transferMut.mutate({ channel: call.channel, destExten: dest.trim() });
      }
    },
  };

  // Estadísticas
  const stats = {
    total: extensions.length,
    active: activeCalls.filter(c => c.state === 'active').length,
    ringing: activeCalls.filter(c => c.state === 'ringing').length,
    available: extensions.filter(e => !e.activeCalls?.length && e.presence_status === 'available').length,
  };

  return (
    <div className="space-y-5">
      {/* Barra de escucha activa: siempre visible mientras se está monitoreando */}
      {isListening && (
        <div className="flex items-center justify-between gap-3 px-4 py-3 rounded-xl bg-blue-950/60 border border-blue-700/50 animate-pulse-slow">
          <div className="flex items-center gap-2.5 min-w-0">
            <Headphones size={18} className="text-blue-300 shrink-0" />
            <span className="text-sm text-blue-200 truncate">
              Escuchando <strong>{activeCalls.find(c => c.channel === listeningChannel)?.callerNum || 'llamada'}</strong> — el agente y el cliente no te oyen
            </span>
          </div>
          <button onClick={stopListening}
            className="shrink-0 flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-red-600 hover:bg-red-700 text-white text-sm font-medium">
            <PhoneOff size={15} /> Detener escucha
          </button>
        </div>
      )}

      {/* Header */}
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h1 className="text-2xl font-bold text-white flex items-center gap-2">
            <Activity size={22} className="text-blue-400" />
            Monitor de Llamadas
          </h1>
          <div className="flex items-center gap-2 mt-0.5">
            <div className={`w-2 h-2 rounded-full ${wsConnected ? 'bg-green-400 animate-pulse' : 'bg-red-400'}`} />
            <span className="text-xs text-muted">
              {wsConnected ? 'Tiempo real activo' : 'Reconectando...'}
            </span>
          </div>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          {/* Selector de extensión supervisor */}
          <div className="flex items-center gap-2">
            <label className="text-xs text-muted whitespace-nowrap" htmlFor="monitor-supervisor-ext">Mi extensión:</label>
            <select
              id="monitor-supervisor-ext"
              value={supervisorExt}
              onChange={e => setSupervisorExt(e.target.value)}
              className="input text-sm py-1.5 min-w-[140px]">
              <option value="">— Seleccionar —</option>
              {extensions.map(e => (
                <option key={e.number} value={e.number}>{e.number} - {e.caller_id_name}</option>
              ))}
            </select>
          </div>
          {/* Vista grid/tabla */}
          <div className="flex bg-gray-800 rounded-lg p-0.5 gap-0.5">
            <button onClick={() => setView('grid')}
              className={`px-3 py-1.5 rounded-md text-xs transition-colors ${view === 'grid' ? 'bg-blue-600 text-white' : 'text-gray-400 hover:text-gray-200'}`}>
              Tarjetas
            </button>
            <button onClick={() => setView('table')}
              className={`px-3 py-1.5 rounded-md text-xs transition-colors ${view === 'table' ? 'bg-blue-600 text-white' : 'text-gray-400 hover:text-gray-200'}`}>
              Tabla
            </button>
          </div>
          <button onClick={() => refetch()} aria-label="Actualizar" className="p-2 text-gray-400 hover:text-white rounded-lg">
            <RefreshCw size={16} />
          </button>
        </div>
      </div>

      {/* Estadísticas en tiempo real */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
        {[
          {
            label: 'Extensiones', value: stats.total, icon: Phone, color: 'text-white',
            sub: `${stats.available} listas para atender`, subColor: 'text-muted', SubIcon: UserCheck,
          },
          {
            label: 'En llamada', value: stats.active, icon: PhoneIncoming, color: 'text-green-400',
            sub: stats.total ? `${Math.round((stats.active / stats.total) * 100)}% de ocupación` : 'sin actividad',
            subColor: 'text-green-500/80', SubIcon: Activity,
          },
          {
            label: 'Timbrando', value: stats.ringing, icon: AlertCircle,
            color: stats.ringing > 0 ? 'text-yellow-400' : 'text-white',
            sub: stats.ringing > 0 ? 'llamadas esperando' : 'ninguna en espera',
            subColor: stats.ringing > 0 ? 'text-yellow-500/80' : 'text-muted', SubIcon: Clock,
          },
          {
            label: 'Disponibles', value: `${stats.available}/${stats.total}`, icon: UserCheck, color: 'text-blue-400',
            sub: `${Math.max(0, stats.total - stats.available - stats.active)} en pausa u otro estado`,
            subColor: 'text-muted', SubIcon: Coffee,
          },
        ].map(s => {
          const Icon = s.icon; const SubIcon = s.SubIcon;
          return (
            <div key={s.label}
              className="relative rounded-xl p-4 bg-gray-900/60 border border-gray-800 overflow-hidden">
              <Icon size={76} className="absolute -right-3 -top-3 text-white opacity-[0.04] pointer-events-none" />
              <div className="text-[11px] uppercase tracking-wider text-muted font-medium">{s.label}</div>
              <div className={`text-[34px] leading-tight font-bold mt-0.5 ${s.color}`} style={{ fontVariantNumeric: 'tabular-nums' }}>
                {s.value}
              </div>
              <div className={`flex items-center gap-1.5 mt-2 text-xs ${s.subColor}`}>
                <SubIcon size={12} className="shrink-0" />
                <span className="truncate">{s.sub}</span>
              </div>
            </div>
          );
        })}
      </div>

      {!supervisorExt && (
        <div className="p-3 bg-yellow-900/20 border border-yellow-700/40 rounded-xl flex items-center gap-2">
          <AlertCircle size={14} className="text-yellow-400 shrink-0" />
          <p className="text-sm text-yellow-300">
            Selecciona <strong>tu extensión</strong> arriba para que los botones de Escuchar, Susurrar e Irrumpir llamen a tu teléfono o softphone.
          </p>
        </div>
      )}

      {isError ? (
        <div className="flex flex-col items-center gap-3 py-16 text-center">
          <AlertCircle size={28} className="text-red-400" />
          <p className="text-sm text-red-300 font-medium">No se pudo cargar el estado de llamadas</p>
          <p className="text-xs text-muted max-w-sm">No se puede confirmar si hay llamadas en curso ahora mismo — no lo interpretes como "sin llamadas activas".</p>
          <button onClick={() => refetch()} className="btn-secondary text-sm">
            <RefreshCw size={14} className="mr-1.5 inline" /> Reintentar
          </button>
        </div>
      ) : isLoading ? (
        <div className="flex justify-center py-16"><div className="spinner" /></div>
      ) : view === 'grid' ? (
        /* Vista en tarjetas */
        <div>
          <div className="flex items-center justify-between mb-3">
            <h2 className="text-sm font-medium text-gray-400">Estado de extensiones</h2>
            <div className="flex items-center gap-3 text-xs text-muted">
              <span className="flex items-center gap-1"><span className="w-2 h-2 rounded-full bg-green-400" /> En llamada</span>
              <span className="flex items-center gap-1"><span className="w-2 h-2 rounded-full bg-yellow-400" /> Timbrando</span>
              <span className="flex items-center gap-1"><span className="w-2 h-2 rounded-full bg-gray-600" /> Libre</span>
            </div>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-3">
            {extensions.map(ext => (
              <ExtensionCard
                key={ext.id}
                ext={ext}
                supervisorExt={supervisorExt}
                canMonitor={userCanMonitor}
                onSpy={ch => doAction(ch, 'listen')}
                onWhisper={ch => doAction(ch, 'whisper')}
                onBarge={ch => doAction(ch, 'barge')}
                onHangup={ch => confirmedHangup(ch)}
                onMenu={openCtxMenu}
              />
            ))}
          </div>
        </div>
      ) : (
        /* Vista en tabla */
        <div className="card">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-sm font-medium text-gray-300">
              Llamadas activas — <span className="text-blue-400">{activeCalls.length}</span>
            </h2>
          </div>
          <ActiveCallsTable
            calls={activeCalls}
            onSpy={ch => doAction(ch, 'listen')}
            onWhisper={ch => doAction(ch, 'whisper')}
            onBarge={ch => doAction(ch, 'barge')}
            onHangup={ch => confirmedHangup(ch)}
            onRowMenu={openCtxMenu}
          />
        </div>
      )}

      {/* Leyenda de acciones */}
      <div className="grid grid-cols-3 gap-3 text-xs">
        {[
          { icon: Headphones, color: 'text-blue-400', bg: 'bg-blue-900/20 border-blue-800/40', title: 'Escuchar (Spy)', desc: 'El supervisor oye la llamada sin que el agente ni el cliente lo sepan. Modo silencioso.' },
          { icon: MessageSquare, color: 'text-purple-400', bg: 'bg-purple-900/20 border-purple-800/40', title: 'Susurrar (Whisper)', desc: 'El supervisor puede hablarle al agente. Solo el agente escucha al supervisor, el cliente no.' },
          { icon: Users, color: 'text-orange-400', bg: 'bg-orange-900/20 border-orange-800/40', title: 'Irrumpir (Barge)', desc: 'El supervisor entra en la llamada. Todos se escuchan: supervisor, agente y cliente.' },
        ].map(item => {
          const Icon = item.icon;
          return (
            <div key={item.title} className={`p-3 rounded-xl border ${item.bg}`}>
              <div className="flex items-center gap-2 mb-1">
                <Icon size={13} className={item.color} />
                <span className={`font-semibold ${item.color}`}>{item.title}</span>
              </div>
              <p className="text-muted leading-relaxed">{item.desc}</p>
            </div>
          );
        })}
      </div>

      {/* Menú contextual de acciones sobre la llamada */}
      <CallContextMenu menu={ctxMenu} onClose={closeCtxMenu} actions={ctxActions} />
    </div>
  );
}
